import type { CommandOf, CommandResult } from '@/core/app/commands';
import type { DomainSystem } from '@/core/app/dispatch';
import type { ContentRegistry } from '@/core/content/registry';
import type { NeighborhoodStoryDef, NeighborhoodStoryId } from '@/core/content/schemas/neighborhood';
import type { GameSave } from '@/core/persistence/schema/v1';
import type { DomainEvent } from '@/core/events/types';
import type { HandlerEnv } from './placement/handlers';
import { grantRewards } from './rewards';
import { dayKeyOf } from './time';

export function storyStage(s: GameSave, id: NeighborhoodStoryId): number {
  return s.story.neighborhood[id]?.stage ?? 0;
}

export function neighborhoodAvailable(s: GameSave, story: NeighborhoodStoryDef): boolean {
  return s.scenes.includes('minimart') && (!story.requiresStory || storyStage(s, story.requiresStory) === 4);
}

/** Current state counts, so existing decorations and supplies count without being redone. */
export function neighborhoodProgress(content: ContentRegistry, s: GameSave, story: NeighborhoodStoryDef) {
  const stage = storyStage(s, story.id);
  let current: number;
  let target = 1;
  if (stage === 0) current = s.lastSession.sceneId === 'minimart' ? 1 : 0;
  else if (stage === 1) {
    target = story.supplies.reduce((n, line) => n + line.qty, 0);
    current = story.supplies.reduce(
      (n, line) => n + Math.min(line.qty, s.inventory.productStacks[line.itemId] ?? 0),
      0,
    );
  } else if (stage === 2) {
    target = story.decorationCount;
    // One coherent corner in either room; duplicates cannot replace a whole set.
    current = Math.max(
      ...(['home', 'minimart', 'studio'] as const).map(
        (roomId) =>
          new Set(
            Object.values(s.inventory.instances)
              .filter(
                (i) =>
                  i.location.kind === 'placed' &&
                  i.location.roomId === roomId &&
                  content.items.get(i.itemId)?.setId === story.setId,
              )
              .map((i) => i.itemId),
          ).size,
      ),
    );
  } else if (stage === 3) {
    target = story.customerCount;
    current = Math.max(
      0,
      s.shop.ledgerSummary.salesCount -
        (s.story.neighborhood[story.id]?.salesBaseline ?? s.shop.ledgerSummary.salesCount),
    );
  } else current = 1;
  return {
    stage,
    current: Math.min(target, current),
    target,
    ready: neighborhoodAvailable(s, story) && stage < 4 && current >= target,
  };
}

/** Boot and unlock reactions: permanent special mail, outside the capped daily-mail cycle. */
export const neighborhoodSystem: DomainSystem = (draft, _events, env) => {
  const events: DomainEvent[] = [];
  for (const story of env.content.neighborhoodStories) {
    if (!neighborhoodAvailable(draft, story)) continue;
    const mailId = `mail_special_neighborhood_${story.id}`;
    if (
      draft.rewardKeys.includes(`mail:${mailId}`) ||
      draft.story.letters.includes(story.inviteLetterId) ||
      draft.mailbox.special.some((m) => m.mailId === mailId)
    )
      continue;
    draft.mailbox.special.push({
      mailId,
      grantedForDayKey: dayKeyOf(env.nowWallMs),
      cycleDay: 0,
      kindForUi: 'npcLetter',
      rewards: [{ type: 'letter', letterId: story.inviteLetterId }],
    });
    events.push({
      type: 'MAIL_RECEIVED',
      mailIds: [mailId],
      pending: draft.mailbox.pending.length + draft.mailbox.special.length,
    });
  }
  return events;
};

/** The expected stage is a durable idempotency token; an old tap can never claim the following step. */
export function advanceNeighborhood(
  draft: GameSave,
  cmd: CommandOf<'advanceNeighborhood'>,
  env: HandlerEnv,
): CommandResult {
  const story = env.content.neighborhoodStories.find((s) => s.id === cmd.storyId);
  if (!story) return { ok: false, error: 'NOT_FOUND' };
  const stage = storyStage(draft, story.id);
  if (!Number.isInteger(cmd.stage) || cmd.stage < 0 || cmd.stage > 3)
    return { ok: false, error: 'INVALID_ARGUMENT' };
  if (cmd.stage < stage) return { ok: true, events: [], replayed: true };
  if (!neighborhoodAvailable(draft, story)) return { ok: false, error: 'LOCKED' };
  if (cmd.stage !== stage || !neighborhoodProgress(env.content, draft, story).ready)
    return { ok: false, error: 'CONDITIONS_NOT_MET' };
  if (stage === 1)
    for (const line of story.supplies)
      draft.inventory.productStacks[line.itemId] =
        (draft.inventory.productStacks[line.itemId] ?? 0) - line.qty;
  draft.story.neighborhood[story.id] = {
    stage: stage + 1,
    salesBaseline:
      stage === 0
        ? draft.shop.ledgerSummary.salesCount
        : (draft.story.neighborhood[story.id]?.salesBaseline ?? draft.shop.ledgerSummary.salesCount),
  };
  const rewards = story.steps[stage]?.rewards ?? [];
  return {
    ok: true,
    events: [
      ...grantRewards(draft, env.content, `neighborhood:${story.id}:${stage}`, rewards, 'story'),
      { type: 'NEIGHBORHOOD_ADVANCED', storyId: story.id, stage: stage + 1 },
    ],
  };
}
